import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import i18next from 'i18next'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import { userInfoApi } from 'src/redux/api/graphql/userInfoApi'
import { restApi } from 'src/redux/api/rest/restApi'
import { fetchTransactionsPerAccount } from 'src/redux/slices/banking'
import { clearClientsData } from 'src/redux/slices/clients'
import { createFreelancerArchiveProject } from 'src/redux/slices/projects'
import {
	createCompanyInfo,
	createFreelancerInfo,
	createUserInfo,
	setSubscriptionStatus,
} from 'src/redux/slices/settings'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type { ReduxCompany, ReduxUserInfo } from 'src/types/apiType'
import type { ApiStatus, UserInfo } from 'src/types/miscType'
import { Auth } from 'src/utils/amplify/auth'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	makeErrorToast,
	makeSuccessToast,
	makeWarningToast,
} from 'src/utils/makeToast'
import { getEmailDomain } from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { tryCatch } from 'src/utils/tryCatch'

type NewUserInfo = {
	email?: string
	group?: string
	firstName?: string
	lastName?: string
	phone?: string
	companyName?: string
	language?: string
}

type AuthState = {
	userLoginStatus: boolean
	newPasswordRequired: boolean
	userInfo: UserInfo
	newUserInfo: NewUserInfo
	userTableInfo: ReduxUserInfo | null
	initialLoginApiStatus: ApiStatus
	loginApiStatus: ApiStatus
	signUpApiStatus: ApiStatus
	updatePasswordApiStatus: ApiStatus
	confirmApiStatus: ApiStatus
	forgotPasswordApiStatus: ApiStatus
	userLogoutApiStatus: ApiStatus
	isOffline: boolean
	error?: string | null
}

const initialState: AuthState = {
	userLoginStatus: false,
	newPasswordRequired: false,
	userInfo: {},
	newUserInfo: {},
	userTableInfo: null,
	initialLoginApiStatus: 'idle',
	loginApiStatus: 'idle',
	signUpApiStatus: 'idle',
	updatePasswordApiStatus: 'idle',
	confirmApiStatus: 'idle',
	forgotPasswordApiStatus: 'idle',
	userLogoutApiStatus: 'idle',
	isOffline: false,
	error: null,
}

export const loginUser = createAsyncThunk<
	UserInfo,
	{ email: string },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>('auth/loginUser', async ({ email }, { dispatch, rejectWithValue }) => {
	try {
		const user = await Auth.currentAuthenticatedUser()
		// If user is using a temporary password we signout the user and they need to update their password
		if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
			makeWarningToast('Password Reset Required!')
			await Auth.signOut()
			return rejectWithValue('newPasswordRequired')
		}
		// Gets the relevant userInfo from the cognito user object
		const userInfo: UserInfo = {
			username: user.username,
			groups: user.signInUserSession.accessToken.payload[
				'cognito:groups'
			],
			cognitoGroup:
				user.signInUserSession.accessToken.payload['cognito:groups'],
			tenantId: user.attributes['custom:tenantid'],
			currentCompanyTenantId: user.attributes['custom:tenantid'],
			currentCompanyName: user.attributes['custom:companyName'],
			phone: user.attributes.phone_number,
			id: user.attributes.sub,
			attributes: { ...user.attributes },
		}

		// If logged in user is a companyAdmin we check and create the companyEmailId for sending invoices if required.
		if (userInfo.groups?.includes('companyAdmin')) {
			const {
				data: { getCompany: companyInfoDetails },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userInfo.currentCompanyTenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			// We check if the user has a companyEmailId to send ocr invoice email to
			if (companyInfoDetails?.id && !companyInfoDetails?.companyEmailId) {
				if (!userInfo || !userInfo?.username) {
					captureExceptionAndThrow(
						new Error('User Info username is undefined'),
						{
							location: 'auth.loginUser.ensureCompanyEmailId',
							userId: userInfo?.id,
							tenantId: userInfo?.currentCompanyTenantId,
						}
					)
				}
				const emailDomain = getEmailDomain(userInfo.username)
				console.log('userInfo.username: ', userInfo.username)
				console.log('emailDomain: ', emailDomain)
				await API.graphql(
					graphqlOperation(mutations.createUniqueEmailId, {
						companyId: companyInfoDetails.id,
						emailDomain,
					})
				)
			}
		}

		const { data: getUserInfo } = await dispatch(
			userInfoApi.endpoints.fetchUserInfo.initiate({
				userInfoId: user.attributes.sub,
			})
		)

		// We fetch from the userInfo table to get the currentCompanyTenantId for companyMultipleTenantAdmin & freelancer
		if (
			userInfo.groups?.includes('companyMultipleTenantAdmin') ||
			userInfo.groups?.includes('companyAdmin') ||
			userInfo.groups?.includes('freelancer')
		) {
			try {
				if (!getUserInfo) {
					// * We are assuming that if the user is a companyMultipleTenantAdmin
					// * They will already have userInfo and companyTable row created
					if (
						userInfo?.groups?.includes('companyMultipleTenantAdmin')
					) {
						// ! No createOrModifyCurrencyRate on update password what are the implications of that?
						// ! In addition to no createUserInfo or createCompany
						return userInfo
					}
					// If user is a freelance we create the freelance table row and a archive project for the freelancer
					if (userInfo?.groups?.includes('freelancer')) {
						// Todo  Might not be needed in the future
						const freelancerInfoResult = await dispatch(
							createFreelancerInfo({ userInfo, email })
						)
						if (
							createFreelancerInfo.rejected.match(
								freelancerInfoResult
							)
						) {
							const message = getErrorMessage(
								freelancerInfoResult.payload
							)
							Sentry.captureException(
								new Error(
									`Failed to create freelancer info: ${message}`
								)
							)
							console.error(
								'createFreelancerInfo failed: ',
								message
							)
						}
						const archiveProjectResult = await dispatch(
							createFreelancerArchiveProject(userInfo)
						)
						if (
							createFreelancerArchiveProject.rejected.match(
								archiveProjectResult
							)
						) {
							const message = getErrorMessage(
								archiveProjectResult.payload
							)
							Sentry.captureException(
								new Error(
									`Failed to create freelancer archive project: ${message}`
								)
							)
							console.error(
								'createFreelancerArchiveProject failed: ',
								message
							)
						}
					}
					// If user is a companyAdmin we create the company table row and save the currency rate
					if (userInfo?.groups?.includes('companyAdmin')) {
						const companyInfoResult = await dispatch(
							createCompanyInfo(userInfo)
						)
						if (
							createCompanyInfo.rejected.match(companyInfoResult)
						) {
							const message = getErrorMessage(
								companyInfoResult.payload
							)
							Sentry.captureException(
								new Error(
									`Failed to create company info: ${message}`
								)
							)
							console.error('createCompanyInfo failed: ', message)
						}
					}
					// Await createUserInfo to ensure it exists in the database before login completes
					const createUserInfoResult = await dispatch(
						createUserInfo(userInfo)
					)
					if (createUserInfo.rejected.match(createUserInfoResult)) {
						const message = getErrorMessage(
							createUserInfoResult.payload
						)
						Sentry.captureException(
							new Error(`Failed to create user info: ${message}`)
						)
						console.error('createUserInfo failed: ', message)
						// If createUserInfo fails, reject the login
						return rejectWithValue(
							`Failed to create user info: ${message}`
						)
					}
					// Refetch getUserInfo to ensure it's available after creation
					const { data: reFetchedUserInfo } = await dispatch(
						userInfoApi.endpoints.fetchUserInfo.initiate(
							{
								userInfoId: user.attributes.sub,
							},
							{
								forceRefetch: true,
							}
						)
					)
					// Update userInfo with reFetched data if available
					if (reFetchedUserInfo) {
						userInfo.currentCompanyTenantId =
							reFetchedUserInfo.currentCompanyTenantId
						userInfo.companyTenantId =
							reFetchedUserInfo.companyTenantId ?? undefined
						userInfo.currentCompanyName =
							reFetchedUserInfo.currentCompanyName ?? undefined
						// Set language from reFetched userInfo for new users
						i18next.changeLanguage(
							reFetchedUserInfo.userLanguage || 'fr'
						)
						if (userInfo?.groups?.includes('teamAdmin')) {
							userInfo.teamIds = reFetchedUserInfo.teamIds
						}
						if (userInfo?.groups?.includes('subTeamAdmin')) {
							userInfo.subTeamIds = reFetchedUserInfo.subTeamIds
						}
					}
					return userInfo
				}
				if (!getUserInfo?.currentCompanyTenantId) {
					captureExceptionAndThrow(
						new Error(
							'User Info currentCompanyTenantId is undefined'
						),
						{
							location: 'auth.loginUser.ensureCompanySelection',
							userId: userInfo.id,
							email: email,
						}
					)
				}
				userInfo.currentCompanyTenantId =
					getUserInfo.currentCompanyTenantId
				userInfo.companyTenantId =
					getUserInfo.companyTenantId ?? undefined
				userInfo.currentCompanyName =
					getUserInfo.currentCompanyName ?? undefined
			} catch (err) {
				const message = getErrorMessage(err)
				Sentry.captureException(err)
				console.error('get UserInfo login: ', message)
			}
		}
		i18next.changeLanguage(getUserInfo?.userLanguage || 'fr')
		if (userInfo?.groups?.includes('teamAdmin')) {
			userInfo.teamIds = getUserInfo?.teamIds
		}
		if (userInfo?.groups?.includes('subTeamAdmin')) {
			userInfo.subTeamIds = getUserInfo?.subTeamIds
		}
		return userInfo
	} catch (err: any) {
		const message = getErrorMessage(err)
		const tErrors = i18next.getFixedT(null, 'errors')
		console.log('login user rejected: ', message)
		if (err.code === 'UserNotFoundException') {
			makeErrorToast(tErrors('userDoesNotExist'))
		} else if (err?.name === 'QuotaExceededError') {
			window.localStorage.clear()
			makeErrorToast(tErrors('loginAgain'))
		} else {
			makeErrorToast(err.message)
		}
		return rejectWithValue(err)
	}
})

export const signUpCompanySuperUser = createAsyncThunk<
	string,
	{
		email: string
		password: string
		firstName: string
		lastName: string
		companyName: string
		phoneNumber: string
		language: string
	},
	{
		rejectValue: string
	}
>(
	'auth/signUpCompanySuperUser',
	async (
		{
			email,
			firstName,
			lastName,
			phoneNumber,
			companyName,
			password,
			language,
		},
		{ rejectWithValue }
	) => {
		try {
			const tenantId = nanoid()
			await Auth.signUp({
				username: email,
				password,
				attributes: {
					email,
					name: `${firstName} ${lastName}`,
					phone_number: phoneNumber, // Now properly formatted with country code
					'custom:tenantid': tenantId,
					'custom:companyName': companyName,
				},
				clientMetadata: {
					group: 'companySuperUser',
					language,
				},
			})
			return email
		} catch (err: any) {
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const signUpFreelanceUser = createAsyncThunk<
	string,
	{
		email: string
		password: string
		firstName: string
		lastName: string
		phoneNumber: string
		language: string
	},
	{
		rejectValue: string
	}
>(
	'auth/signUpFreelanceUser',
	async ({ email, password, language }, { rejectWithValue }) => {
		if (!language) {
			makeErrorToast('Please contact contact@chedar.fr for assistance')
			return rejectWithValue('Language is required')
		}
		try {
			const tenantId = nanoid()
			await Auth.signUp({
				username: email,
				password,
				attributes: {
					email,
					'custom:tenantid': tenantId,
				},
				clientMetadata: {
					group: 'freelancer',
					language,
				},
			})
			return email
		} catch (err: any) {
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const updatePassword = createAsyncThunk<
	UserInfo,
	{ email: string; currentPassword: string; newPassword: string },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'auth/updatePassword',
	async (
		{ email, currentPassword, newPassword },
		{ dispatch, rejectWithValue }
	) => {
		try {
			const { data: user, error } = await tryCatch(
				Auth.signIn(email, currentPassword, {
					clientMetadata: {
						passwordUpdate: 'true',
					},
				})
			)
			if (error) {
				console.error('error: ', error)
				makeErrorToast(getErrorMessage(error))
				return rejectWithValue(getErrorMessage(error))
			}
			console.log('user: ', user)
			// Temp password is updated with newPassword
			if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
				const {
					data: completeNewPassword,
					error: completeNewPasswordError,
				} = await tryCatch(
					Auth.completeNewPassword(
						user,
						newPassword,
						{},
						{
							passwordUpdate: 'true',
						}
					)
				)
				console.log('completeNewPassword: ', completeNewPassword)
				if (completeNewPasswordError) {
					console.error(
						'completeNewPasswordError: ',
						completeNewPasswordError
					)
					makeErrorToast(getErrorMessage(completeNewPasswordError))
					return rejectWithValue(
						getErrorMessage(completeNewPasswordError)
					)
				}
			}
			let userInfo: UserInfo = {
				username: user.username,
				groups: user.signInUserSession.accessToken.payload[
					'cognito:groups'
				],
				cognitoGroup:
					user.signInUserSession.accessToken.payload[
						'cognito:groups'
					],
				tenantId: user.challengeParam.userAttributes['custom:tenantid'],
				companyTenantId: [
					user.challengeParam.userAttributes['custom:tenantid'],
				],
				currentCompanyTenantId:
					user.challengeParam.userAttributes['custom:tenantid'],
				//*  We use cognito sub as the id for userinfo table which is used by companyMultipleTenantAdmin
				id: user.signInUserSession.accessToken.payload.sub,
				attributes: {
					sub: user.signInUserSession.accessToken.payload.sub,
					email: user.username,
				},
			}
			const userExists = (await API.graphql(
				graphqlOperation(queries.getUserInfo, {
					id: userInfo.id,
				})
			)) as { data: { getUserInfo: ReduxUserInfo } }

			if (userExists.data.getUserInfo?.tenantId) {
				userInfo = {
					...userInfo,
					companyTenantId:
						userExists.data.getUserInfo.companyTenantId,
					tenantId: userExists.data.getUserInfo.tenantId,
					currentCompanyTenantId:
						userExists.data.getUserInfo.currentCompanyTenantId,
					currentCompanyName:
						userExists.data.getUserInfo.currentCompanyName,
					teamIds: userExists.data.getUserInfo.teamIds,
					subTeamIds: userExists.data.getUserInfo.subTeamIds,
					projectIds: userExists.data.getUserInfo.projectIds,
				}
			}
			// * We are assuming that if the user is a companyMultipleTenantAdmin
			// * They will already have userInfo and companyTable row created
			if (userInfo?.groups?.includes('companyMultipleTenantAdmin')) {
				// ! No createOrModifyCurrencyRate on update password what are the implications of that?
				// ! In addition to no createUserInfo or createCompany
				return userInfo
			}
			// If user is a freelance we create the freelance table row and a archive project for the freelancer
			if (userInfo?.groups?.includes('freelancer')) {
				// Check first if the user is present in the db

				// Todo  Might not be needed in the future
				dispatch(createFreelancerInfo({ userInfo, email }))
				dispatch(createFreelancerArchiveProject(userInfo))
			}
			// If user is a companyAdmin we create the company table row and save the currency rate
			if (userInfo?.groups?.includes('companyAdmin')) {
				dispatch(createCompanyInfo(userInfo))
			}
			dispatch(createUserInfo(userInfo))

			return userInfo
		} catch (err: any) {
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const confirmCode = createAsyncThunk<
	void,
	{ confirmationCode: string },
	{
		state: RootState
		rejectValue: string
	}
>(
	'auth/confirmCode',
	async ({ confirmationCode }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			if (!auth.newUserInfo.email || !auth.newUserInfo.group) {
				captureExceptionAndThrow(
					new Error('User email/group not found in confirm code'),
					{
						location: 'auth.confirmCode',
						email: auth.newUserInfo.email,
						group: auth.newUserInfo.group,
						newUserInfo: auth.newUserInfo,
					}
				)
			}
			await Auth.confirmSignUp(auth.newUserInfo.email, confirmationCode, {
				clientMetadata: {
					group: auth.newUserInfo.group,
					firstName: auth.newUserInfo.firstName ?? '',
					lastName: auth.newUserInfo.lastName ?? '',
					phone: auth.newUserInfo.phone ?? '',
					companyName: auth.newUserInfo.companyName ?? '',
					language: auth.newUserInfo.language ?? 'fr',
				},
			})
			makeSuccessToast('Successfully Signed Up. Please Login')
		} catch (err: any) {
			if (err.code === 'UserNotConfirmedException') {
				makeErrorToast(err.message)
				return rejectWithValue('notConfirmed')
			}
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const resendConfirmCode = createAsyncThunk<
	void,
	void,
	{
		state: RootState
		rejectValue: string
	}
>(
	'auth/resendConfirmCode',
	async (actualGrandTotal, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			if (!auth.newUserInfo.email) {
				captureExceptionAndThrow(
					new Error('User email not found in confirm code'),
					{
						location: 'auth.resendConfirmCode',
						newUserInfo: auth.newUserInfo,
					}
				)
			}
			await Auth.resendSignUp(auth.newUserInfo.email)
			makeSuccessToast('Resent confirmation code')
		} catch (err: any) {
			if (err.code === 'UserNotConfirmedException') {
				makeErrorToast(err.message)
				return rejectWithValue('notConfirmed')
			}
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const checkAuthenticationStatus = createAsyncThunk<
	UserInfo,
	undefined,
	{ rejectValue: string; dispatch: ThunkAppDispatch }
>(
	'auth/checkAuthenticationStatus',
	async (arg, { rejectWithValue, dispatch }) => {
		try {
			const user = await Auth.currentAuthenticatedUser()
			console.log('checkAuthenticationStatus user: ', user)
			if (
				user.signInUserSession.accessToken.payload[
					'cognito:groups'
				].includes('companySuperUser')
			) {
				dispatch(
					fetchTransactionsPerAccount({
						currentTenantId: user.attributes['custom:tenantid'],
					})
				)
			}

			const userInfo: UserInfo = {
				username: user.username,
				groups: user.signInUserSession.accessToken.payload[
					'cognito:groups'
				],
				cognitoGroup:
					user.signInUserSession.accessToken.payload[
						'cognito:groups'
					],
				tenantId: user.attributes['custom:tenantid'],
				currentCompanyTenantId: user.attributes['custom:tenantid'],
				id: user.attributes.sub,
				attributes: { ...user.attributes },
			}
			const { data: getUserInfo } = await dispatch(
				userInfoApi.endpoints.fetchUserInfo.initiate({
					userInfoId: user.attributes.sub,
				})
			)

			// We fetch from the userInfo table to get the currentCompanyTenantId for companyMultipleTenantAdmin & freelancer
			if (
				userInfo.groups?.includes('companyMultipleTenantAdmin') ||
				userInfo.groups?.includes('freelancer')
			) {
				if (!getUserInfo?.currentCompanyTenantId) {
					captureExceptionAndThrow(
						new Error(
							'User Info currentCompanyTenantId is undefined'
						),
						{
							location: 'auth.checkAuthenticationStatus',
							userId: userInfo.id,
							email: userInfo.username,
							groups: userInfo.groups,
						}
					)
				}
				userInfo.currentCompanyTenantId =
					getUserInfo.currentCompanyTenantId
				userInfo.companyTenantId =
					getUserInfo.companyTenantId ?? undefined
				userInfo.currentCompanyName =
					getUserInfo.currentCompanyName ?? undefined
			}
			if (userInfo?.groups?.includes('freelancer')) {
				userInfo.tenantId = getUserInfo?.tenantId ?? undefined
			}
			if (userInfo?.groups?.includes('teamAdmin')) {
				userInfo.teamIds = getUserInfo?.teamIds
			}
			if (userInfo?.groups?.includes('subTeamAdmin')) {
				userInfo.subTeamIds = getUserInfo?.subTeamIds
			}
			i18next.changeLanguage(getUserInfo?.userLanguage || 'fr')
			return userInfo
		} catch (err) {
			const message = getErrorMessage(err)
			return rejectWithValue(message)
		}
	}
)

export const logOutUser = createAsyncThunk<
	void,
	undefined,
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>('auth/logOutUser', async (arg, { dispatch, rejectWithValue }) => {
	try {
		await Auth.signOut()
		dispatch(clearClientsData())
		dispatch(setSubscriptionStatus('idle'))
		// Clear all RTK Query cache data
		dispatch(graphqlApi.util.resetApiState())
		dispatch(restApi.util.resetApiState())
	} catch (err: any) {
		makeErrorToast(err.message)
		return rejectWithValue(err)
	}
})

export const forgotPassword = createAsyncThunk<
	void,
	string,
	{ rejectValue: string }
>('auth/forgotPassword', async (username, { rejectWithValue }) => {
	const tErrors = i18next.getFixedT(null, 'errors')
	try {
		await Auth.forgotPassword(username)
	} catch (err: any) {
		if (err.code === 'UserNotFoundException') {
			makeErrorToast(tErrors('usernameDoesNotExist'))
			return rejectWithValue('usernameDoesNotExist')
		}
		makeErrorToast(err.message)
		return rejectWithValue(err)
	}
})

export const confirmForgotPassword = createAsyncThunk<
	void,
	{ confirmationCode: string; newPassword: string },
	{ state: RootState; rejectValue: string }
>(
	'auth/confirmForgotPassword',
	async (
		{ confirmationCode, newPassword },
		{ rejectWithValue, getState }
	) => {
		const { auth } = getState()
		try {
			if (!auth.newUserInfo.email) {
				captureExceptionAndThrow(
					new Error('Email not found for confirmForgotPassword'),
					{
						location: 'auth.confirmForgotPassword',
						newUserInfo: auth.newUserInfo,
					}
				)
			}
			await Auth.forgotPasswordSubmit(
				auth.newUserInfo.email,
				confirmationCode,
				newPassword
			)
			const tSettings = i18next.getFixedT(null, 'settings')
			makeSuccessToast(tSettings('toasts.passwordUpdated'))
		} catch (err: any) {
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const changePassword = createAsyncThunk<
	void,
	{ currentPassword: string; newPassword: string },
	{ rejectValue: string }
>(
	'auth/changePassword',
	async ({ currentPassword, newPassword }, { rejectWithValue }) => {
		try {
			const user = await Auth.currentAuthenticatedUser()
			await Auth.changePassword(user, currentPassword, newPassword)
			const tSettings = i18next.getFixedT(null, 'settings')
			makeSuccessToast(tSettings('toasts.passwordUpdated'))
		} catch (err: any) {
			makeErrorToast(err.message)
			return rejectWithValue(err)
		}
	}
)

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setIsOffline: (state, action) => {
			state.isOffline = action.payload.status
		},
		setUserInfoCompanyTenantId: (state, action) => {
			state.userInfo = {
				...state.userInfo,
				currentCompanyTenantId: action.payload,
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(loginUser.pending, (state, action) => {
			state.loginApiStatus = 'loading'
		})
		builder.addCase(loginUser.fulfilled, (state, action) => {
			state.loginApiStatus = 'succeeded'
			state.confirmApiStatus = 'idle'
			state.userLoginStatus = true
			state.userInfo = { ...action.payload }
		})
		builder.addCase(loginUser.rejected, (state, action) => {
			state.loginApiStatus = 'failed'
			state.userLoginStatus = false
			state.userInfo = {}
			if (action.payload === 'newPasswordRequired') {
				state.newPasswordRequired = true
			}
			state.error = action.error.message
		})
		builder.addCase(signUpCompanySuperUser.pending, (state, action) => {
			state.signUpApiStatus = 'loading'
			state.newUserInfo = {
				email: action.meta.arg.email,
				group: 'companySuperUser',
				firstName: action.meta.arg.firstName,
				lastName: action.meta.arg.lastName,
				phone: action.meta.arg.phoneNumber,
				companyName: action.meta.arg.companyName,
				language: action.meta.arg.language,
			}
		})
		builder.addCase(signUpCompanySuperUser.fulfilled, (state, action) => {
			state.signUpApiStatus = 'succeeded'
			state.newUserInfo = {
				email: action.meta.arg.email,
				group: 'companySuperUser',
				firstName: action.meta.arg.firstName,
				lastName: action.meta.arg.lastName,
				phone: action.meta.arg.phoneNumber,
				companyName: action.meta.arg.companyName,
				language: action.meta.arg.language,
			}
		})
		builder.addCase(signUpCompanySuperUser.rejected, (state, action) => {
			state.signUpApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(signUpFreelanceUser.pending, (state, action) => {
			state.signUpApiStatus = 'loading'
			state.newUserInfo = {
				email: action.meta.arg.email,
				group: 'freelancer',
				firstName: action.meta.arg.firstName,
				lastName: action.meta.arg.lastName,
				phone: action.meta.arg.phoneNumber,
				language: action.meta.arg.language,
			}
		})
		builder.addCase(signUpFreelanceUser.fulfilled, (state, action) => {
			state.signUpApiStatus = 'succeeded'
			state.newUserInfo = {
				email: action.meta.arg.email,
				group: 'freelancer',
				firstName: action.meta.arg.firstName,
				lastName: action.meta.arg.lastName,
				phone: action.meta.arg.phoneNumber,
				language: action.meta.arg.language,
			}
		})
		builder.addCase(signUpFreelanceUser.rejected, (state, action) => {
			state.signUpApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(updatePassword.pending, (state, action) => {
			state.updatePasswordApiStatus = 'loading'
		})
		builder.addCase(updatePassword.fulfilled, (state, action) => {
			state.updatePasswordApiStatus = 'succeeded'
			state.userLoginStatus = true
			state.userInfo = { ...action.payload }
		})
		builder.addCase(updatePassword.rejected, (state, action) => {
			state.updatePasswordApiStatus = 'failed'
			state.userLoginStatus = false
			state.userInfo = {}
			state.error = action.error.message
		})
		builder.addCase(forgotPassword.pending, (state, action) => {
			state.forgotPasswordApiStatus = 'loading'
		})
		builder.addCase(forgotPassword.fulfilled, (state, action) => {
			state.forgotPasswordApiStatus = 'succeeded'
			state.newUserInfo = { email: action.meta.arg }
		})
		builder.addCase(forgotPassword.rejected, (state, action) => {
			state.forgotPasswordApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(confirmCode.pending, (state, action) => {
			state.confirmApiStatus = 'loading'
		})
		builder.addCase(confirmCode.fulfilled, (state, action) => {
			state.confirmApiStatus = 'succeeded'
			state.newUserInfo = {}
		})
		builder.addCase(confirmCode.rejected, (state, action) => {
			state.confirmApiStatus = 'failed'
			state.newUserInfo = {}
			state.error = action.error.message
		})
		builder.addCase(confirmForgotPassword.pending, (state, action) => {
			state.confirmApiStatus = 'loading'
		})
		builder.addCase(confirmForgotPassword.fulfilled, (state, action) => {
			state.confirmApiStatus = 'succeeded'
			state.forgotPasswordApiStatus = 'idle'
			state.newUserInfo = {}
		})
		builder.addCase(confirmForgotPassword.rejected, (state, action) => {
			state.confirmApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			checkAuthenticationStatus.fulfilled,
			(state, action) => {
				state.initialLoginApiStatus = 'succeeded'
				state.userLoginStatus = true
				state.userInfo = { ...action.payload }
			}
		)
		builder.addCase(checkAuthenticationStatus.rejected, (state, action) => {
			state.initialLoginApiStatus = 'failed'
			state.userLoginStatus = false
			state.error = action.error.message
		})

		builder.addCase(logOutUser.pending, (state, action) => {
			state.userLogoutApiStatus = 'loading'
		})
		builder.addCase(logOutUser.fulfilled, (state, action) => {
			state.userInfo = {}
			state.newUserInfo = {}
			state.userLoginStatus = false
			state.userLogoutApiStatus = 'succeeded'
			state.userTableInfo = null
		})
		builder.addCase(logOutUser.rejected, (state, action) => {
			state.error = action.error.message
			state.userLoginStatus = false
			state.userLogoutApiStatus = 'failed'
			state.userTableInfo = null
		})
		builder.addMatcher(
			userInfoApi.endpoints.fetchUserInfo.matchFulfilled,
			(state, action) => {
				state.userTableInfo = action.payload
			}
		)
	},
})
export const { setIsOffline, setUserInfoCompanyTenantId } = authSlice.actions

export default authSlice.reducer
